'use client';

// Template gallery for the "Slides" composer mode. Picking a template drops an
// editable draft into the composer (via slideTemplateDraft) — it is NOT
// auto-sent, same discipline as branching drafts — and Slides mode stays
// selected. Pure deck data + the draft builder live in packages/core
// (lib/slide-templates.ts, shared + validity-tested); this is just the UI.
//
// Two entry points, one gallery:
//   • <SlideTemplateGallery> — the inline row shown under the mode cards on the
//     empty hub when Slides is selected.
//   • <SlideTemplatesButton> — a compact "Templates" affordance for the in-chat
//     chips row; opens the same gallery in a sheet.
// Thumbnails are hand-authored CSS mocks keyed by template.thumbnail — no new
// rendering infra, honest and cheap.

import { useState } from 'react';
import { Sparkle } from '@phosphor-icons/react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  SLIDE_TEMPLATES,
  slideTemplateDraft,
  type SlideTemplate,
  type SlideThumbnailKind,
} from '@/lib/slide-templates';

/**
 * Cheap CSS mock of a slide, keyed by layout kind. Uses the template accent so
 * each template reads as its own deck at a glance — no rendered assets.
 */
function SlideThumb({ kind, accent }: { kind: SlideThumbnailKind; accent: string }) {
  const bar = (w: string, opacity = 1) => (
    <div className="h-1 rounded-full" style={{ width: w, backgroundColor: accent, opacity }} />
  );
  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-lg border border-white/[0.06] bg-[#0f0f0f]">
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.14]"
        style={{ background: `radial-gradient(circle at 30% 20%, ${accent}, transparent 60%)` }}
      />
      <div className="relative flex h-full flex-col justify-center gap-1.5 px-3 py-2.5">
        {kind === 'title' && (
          <div className="flex flex-col items-center gap-1.5">
            {bar('58%')}
            <div className="h-2 w-3/5 rounded-sm" style={{ backgroundColor: accent }} />
            {bar('34%', 0.5)}
          </div>
        )}
        {kind === 'quote' && (
          <div className="flex items-center justify-center">
            <div className="h-2.5 w-4/5 rounded-sm" style={{ backgroundColor: accent }} />
          </div>
        )}
        {kind === 'grid' && (
          <div className="grid grid-cols-3 gap-1.5">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="aspect-square rounded-md"
                style={{ backgroundColor: accent, opacity: 0.55 - i * 0.08 }}
              />
            ))}
          </div>
        )}
        {kind === 'chart' && (
          <div className="flex h-full items-end gap-1.5 pb-0.5">
            {[40, 65, 50, 85, 72].map((h, i) => (
              <div
                key={i}
                className="flex-1 rounded-t-sm"
                style={{ height: `${h}%`, backgroundColor: accent, opacity: 0.45 + i * 0.1 }}
              />
            ))}
          </div>
        )}
        {kind === 'bullets' && (
          <div className="flex flex-col gap-1.5">
            {bar('45%')}
            {['70%', '60%', '52%'].map((w, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <div className="size-1 rounded-full" style={{ backgroundColor: accent }} />
                {bar(w, 0.6)}
              </div>
            ))}
          </div>
        )}
        {kind === 'timeline' && (
          <div className="flex items-center gap-0">
            <div className="h-px flex-1" style={{ backgroundColor: accent, opacity: 0.5 }} />
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex items-center">
                <div className="size-2 rounded-full" style={{ backgroundColor: accent }} />
                {i < 3 && <div className="h-px w-6" style={{ backgroundColor: accent, opacity: 0.5 }} />}
              </div>
            ))}
            <div className="h-px flex-1" style={{ backgroundColor: accent, opacity: 0.5 }} />
          </div>
        )}
      </div>
    </div>
  );
}

function TemplateCard({
  template,
  onPick,
}: {
  template: SlideTemplate;
  onPick: (draft: string, template: SlideTemplate) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onPick(slideTemplateDraft(template), template)}
      className="group flex flex-col gap-2 rounded-xl border border-white/[0.06] bg-white/[0.02] p-2 text-left outline-none transition-colors hover:border-violet-500/40 hover:bg-violet-500/[0.06] focus-visible:ring-2 focus-visible:ring-violet-400"
    >
      <SlideThumb kind={template.thumbnail} accent={template.accent} />
      <div className="px-0.5">
        <div className="flex items-center gap-1.5">
          <span className="text-[12.5px]">{template.emoji}</span>
          <span className="truncate text-[12.5px] font-bold text-[#efefef]">{template.name}</span>
          <span className="ml-auto shrink-0 rounded-full bg-white/[0.05] px-1.5 py-0.5 text-[9.5px] font-semibold text-muted-foreground">
            {template.outline.length} slides
          </span>
        </div>
        <p className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-muted-foreground">
          {template.description}
        </p>
      </div>
    </button>
  );
}

/** The gallery grid itself — reused inline on the hub and inside the sheet. */
export function SlideTemplateGallery({
  onPick,
  className,
}: {
  onPick: (draft: string, template: SlideTemplate) => void;
  className?: string;
}) {
  return (
    <div className={`grid grid-cols-2 gap-2 sm:grid-cols-3 ${className ?? ''}`}>
      {SLIDE_TEMPLATES.map((t) => (
        <TemplateCard key={t.id} template={t} onPick={onPick} />
      ))}
    </div>
  );
}

/**
 * Compact "Templates" affordance for the in-chat chips row. Opens the same
 * gallery in a bottom sheet; picking a template inserts the draft and closes.
 */
export function SlideTemplatesButton({
  onPick,
}: {
  onPick: (draft: string, template: SlideTemplate) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-full border border-violet-500/40 bg-violet-500/[0.08] px-3 py-1.5 text-xs font-semibold text-violet-100 outline-none transition-colors hover:bg-violet-500/[0.16] focus-visible:ring-2 focus-visible:ring-violet-400"
      >
        <Sparkle weight="fill" className="size-3.5 shrink-0" />
        <span className="whitespace-nowrap">Templates</span>
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Start from a slide template</SheetTitle>
            <SheetDescription>
              Pick a deck to drop an editable outline into the composer. Nothing sends until
              you review and hit send.
            </SheetDescription>
          </SheetHeader>
          <div className="px-4 pb-6">
            <SlideTemplateGallery
              onPick={(draft, t) => {
                onPick(draft, t);
                setOpen(false);
              }}
            />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
